import { zodResolver } from '@hookform/resolvers/zod'
import type { RefObject } from 'react'
import { type SubmitHandler, useFieldArray, useForm } from 'react-hook-form'
import { InputText } from '@/components/commons/input-text'
import { TextArea } from '@/components/commons/textarea'
import { User01Icon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import { ServiceMerchandisingInputSchema } from '@/features/service-vetting/form-schemas.ts'
import { getOfferingTypeLabel } from '@/features/service-vetting/utils'
import { useTranslation } from '@/translations/i18n'
import { OpsVettingMerchFormHeader } from '../../ops-vetting/components/ops-vetting-merch-form-header'

interface ServiceVettingMerchInputFormProps {
	isReadOnly?: boolean
	defaultValues: Partial<ServiceMerchandisingInputSchema>
	aiSubmissionRef: RefObject<HTMLButtonElement | null>
	handleAiSubmissionCb: SubmitHandler<ServiceMerchandisingInputSchema>
}

export const ServiceVettingMerchInputForm = ({
	isReadOnly = false,
	defaultValues,
	aiSubmissionRef,
	handleAiSubmissionCb
}: ServiceVettingMerchInputFormProps) => {
	const { t } = useTranslation()
	const {
		register,
		control,
		formState: { errors },
		handleSubmit
	} = useForm({
		resolver: zodResolver(ServiceMerchandisingInputSchema),
		defaultValues: defaultValues as ServiceMerchandisingInputSchema
	})
	const { fields } = useFieldArray({ control, name: 'offerings' })

	return (
		<form className='flex-1 p-6'>
			<OpsVettingMerchFormHeader
				description={t('vetting:merch.service.input_form_description')}
				icon={<User01Icon />}
				title={t('vetting:merch.service.input_form_title')}
			/>
			<div className='flex w-full flex-col gap-2'>
				<div className='flex w-full flex-col gap-5 py-6 text-neutral-900'>
					<h4 className='font-medium text-base'>
						{t('vetting:merch.service.form_offering_section_title')}
					</h4>
					<InputText
						disabled={isReadOnly}
						label={t('vetting:merch.service.service_title')}
						type={'text'}
						{...register('service_title')}
					/>
					<TextArea
						disabled={isReadOnly}
						label={t('vetting:merch.service.byline_label')}
						rows={4}
						{...register('highlight_expertise')}
					/>

					{fields.map((offering, index) => (
						<div
							className='flex w-full flex-col gap-2'
							key={offering.id}
						>
							<div className='flex items-center gap-4'>
								<p className='font-medium text-sm'>
									{t(getOfferingTypeLabel(offering.type))}
									{t(
										'vetting:merch.service.offering_duration',
										{
											duration: offering.duration ?? '-'
										}
									)}
								</p>
							</div>
							<InputText
								disabled={isReadOnly}
								label='Title'
								type={'text'}
								{...register(`offerings.${index}.title`)}
							/>
							<TextArea
								disabled={isReadOnly}
								label={t(
									'vetting:merch.service.offering_description_label'
								)}
								rows={4}
								{...register(`offerings.${index}.description`)}
							/>
						</div>
					))}
				</div>

				<Separator orientation='horizontal' />

				<div className='flex w-full flex-col gap-4 py-6 text-neutral-900'>
					<h4 className='font-medium text-base'>
						{t('vetting:merch.service.host_years_of_experience', {
							years: defaultValues?.host_experience ?? '-'
						})}
					</h4>
					<TextArea
						disabled={isReadOnly}
						label={t(
							'vetting:merch.service.experience_summary_label'
						)}
						rows={4}
						{...register('host_experience_summary')}
						errorMessage={errors?.host_experience_summary?.message}
					/>
					<TextArea
						disabled={isReadOnly}
						label={t(
							'vetting:merch.service.experience_highlight_label'
						)}
						rows={4}
						{...register('host_experience_highlight')}
						errorMessage={
							errors?.host_experience_highlight?.message
						}
					/>
					<TextArea
						disabled={isReadOnly}
						label={t(
							'vetting:merch.service.experience_background_label'
						)}
						rows={4}
						{...register('host_experience_background')}
						errorMessage={
							errors?.host_experience_background?.message
						}
					/>
				</div>
			</div>
			<button
				onClick={handleSubmit(handleAiSubmissionCb)}
				ref={aiSubmissionRef}
				style={{ display: 'none' }}
				type='button'
			/>
		</form>
	)
}

ServiceVettingMerchInputForm.displayName = 'ServiceVettingMerchInputForm'
